/**
 * Handles desktop hover interactions, mobile toggle, and image swap effects
 */

class MegaMenu {
    constructor() {
        this._onDesktopOutsideClick = null;
        this._onNovaMobileMenuClose = null;
        this._onMobileOutsideClick = null;
        this._onMobileEscapeKey = null;
        this._onMobileTabKey = null;
        this.init();
    }

    init() {
        this.setupDesktopMenu();
        this.setupMobileMenu();
        this.setupImageHoverEffects();
        this.setupAccessibility();
        this.setupScrollDetection();
        this.setupMobileStickyStack();
    }

    _bindDocument(type, handler) {
        document.addEventListener(type, handler);
    }

    setupDesktopMenu() {
        const menuItems = document.querySelectorAll('.mm-item');

        menuItems.forEach(item => {
            const dropdown = item.querySelector('.mm-dropdown');
            const link = item.querySelector('.mm-link, .mm-link-dropdown');
            if (!dropdown) return;

            let hoverTimeout;
            let leaveTimeout;
            // Track click state on the item itself
            item.isClicked = false;

            // Handle click on main menu item
            if (link) {
                link.addEventListener('click', (e) => {
                    if (dropdown) {
                        e.preventDefault();
                        e.stopPropagation();
                        item.isClicked = true;

                        // Toggle dropdown
                        if (dropdown.classList.contains('active')) {
                            this.closeAllDropdowns();
                        } else {
                            this.closeAllDropdowns();
                            dropdown.classList.add('active');
                            item.classList.add('active');
                        }
                    }
                });
            }

            // Mouse enter
            item.addEventListener('mouseenter', () => {
                clearTimeout(leaveTimeout);
                if (!item.isClicked) {
                    hoverTimeout = setTimeout(() => {
                        this.closeAllDropdowns();
                        dropdown.classList.add('active');
                        item.classList.add('active');
                    }, 150); // Small delay for better UX
                }
            });

            // Mouse leave
            item.addEventListener('mouseleave', (e) => {
                clearTimeout(hoverTimeout);
                // Only auto-close if not clicked and not moving to dropdown
                if (!item.isClicked && !dropdown.contains(e.relatedTarget)) {
                    leaveTimeout = setTimeout(() => {
                        dropdown.classList.remove('active');
                        item.classList.remove('active');
                    }, 300); // Longer delay to allow smooth navigation
                }
            });

            // Keep dropdown open when hovering over it
            dropdown.addEventListener('mouseenter', () => {
                clearTimeout(leaveTimeout);
            });

            dropdown.addEventListener('mouseleave', (e) => {
                // Only close if not moving back to the menu item
                if (!item.contains(e.relatedTarget)) {
                    leaveTimeout = setTimeout(() => {
                        dropdown.classList.remove('active');
                        item.classList.remove('active');
                        item.isClicked = false; // Reset click state when closing
                    }, 200);
                }
            });
        });

        this._onDesktopOutsideClick = (e) => {
            if (!e.target.closest('.mm-item')) {
                this.closeAllDropdowns();
            }
        };
        this._bindDocument('click', this._onDesktopOutsideClick);
    }

    setupMobileMenu() {
        const mobileToggle = document.getElementById('mm-mobile-toggle');
        const mobilePanel = document.getElementById('mm-mobile-panel');
        const mobileOverlay = document.getElementById('mm-mobile-overlay');
        const mobileSubmenuToggles = document.querySelectorAll('.mm-mobile-submenu-toggle');

        if (!mobileToggle || !mobilePanel || !mobileOverlay) {
            console.warn('Mobile menu elements not found');
            return;
        }

        this._onNovaMobileMenuClose = () => {
            if (window.matchMedia('(min-width: 1280px)').matches) return;
            if (!document.querySelector('.mm-wrapper')) return;
            if (!this.isMobileMenuOpen()) return;
            this.closeMobileMenu();
        };
        this._bindDocument('nova:mobile-menu:close', this._onNovaMobileMenuClose);

        const mmToggleBtn = document.getElementById('mm-toggle');
        if (mmToggleBtn) {
            mmToggleBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (this.isMobileMenuOpen()) {
                    this.closeMobileMenu();
                } else {
                    this.openMobileMenu();
                }
            });
        }

        document.getElementById('mm-mobile-close')?.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.closeMobileMenu();
        });

        // Close menu when clicking overlay
        mobileOverlay.addEventListener('click', () => {
            this.closeMobileMenu();
        });

        // Handle mobile dropdown toggles
        mobileSubmenuToggles.forEach(toggle => {
            toggle.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.toggleMobileDropdown(toggle);
            });
        });

        this._onMobileOutsideClick = (e) => {
            if (window.Shopify?.designMode) return;
            if (!e.target.closest('.mm-mobile') && this.isMobileMenuOpen()) {
                this.closeMobileMenu();
            }
        };
        this._bindDocument('click', this._onMobileOutsideClick);

        this._onMobileEscapeKey = (e) => {
            if (e.key === 'Escape' && this.isMobileMenuOpen()) {
                this.closeMobileMenu();
            }
        };
        this._bindDocument('keydown', this._onMobileEscapeKey);
    }

    setupImageHoverEffects() {
        const dropdownImages = document.querySelectorAll('.mm-dropdown-image');

        dropdownImages.forEach(img => {
            const hoverSrc = img.dataset.hover;
            if (!hoverSrc) return;

            let hoverImage = new Image();
            hoverImage.src = hoverSrc;

            img.addEventListener('mouseenter', () => {
                if (hoverImage.complete) {
                    img.src = hoverSrc;
                } else {
                    hoverImage.onload = () => {
                        img.src = hoverSrc;
                    };
                }
            });

            img.addEventListener('mouseleave', () => {
                const originalSrc = img.dataset.src || img.getAttribute('src').replace(hoverSrc, '');
                img.src = originalSrc;
            });
        });
    }

    setupAccessibility() {
        // Add ARIA attributes
        const mobileToggle = document.getElementById('mm-toggle');
        const mobilePanel = document.getElementById('mm-mobile-panel');

        if (mobileToggle && mobilePanel) {
            mobileToggle.setAttribute('aria-expanded', 'false');
            mobilePanel.setAttribute('aria-hidden', 'true');
        }

        this._onMobileTabKey = (e) => {
            if (e.key === 'Tab' && this.isMobileMenuOpen()) {
                this.handleKeyboardNavigation(e);
            }
        };
        this._bindDocument('keydown', this._onMobileTabKey);

        // Update ARIA for desktop dropdowns
        const desktopMenuItems = document.querySelectorAll('.mm-item');
        desktopMenuItems.forEach(item => {
            const link = item.querySelector('.mm-link, .mm-link-dropdown');
            const dropdown = item.querySelector('.mm-dropdown');

            if (link && dropdown) {
                item.addEventListener('mouseenter', () => {
                    link.setAttribute('aria-expanded', 'true');
                });

                item.addEventListener('mouseleave', () => {
                    link.setAttribute('aria-expanded', 'false');
                });
            }
        });
    }

    setupScrollDetection() {
        const header = document.querySelector('.mm-desktop');
        const wrapper = document.querySelector('.mm-wrapper');
        const headerPosition = wrapper?.dataset.headerPosition;

        if (!header || !wrapper || !headerPosition) return;

        const syncScrolledFromTheme = () => {
            if (document.body.hasAttribute('is-scrolled')) {
                header.classList.add('scrolled');
            } else {
                header.classList.remove('scrolled');
            }
        };

        // Same threshold/state as <nova-header> (component-header.js + body[is-scrolled])
        if (headerPosition === 'theme') {
            syncScrolledFromTheme();
            const bodyObserver = new MutationObserver(syncScrolledFromTheme);
            bodyObserver.observe(document.body, {
                attributes: true,
                attributeFilter: ['is-scrolled'],
            });
            this.bodyScrollObserver = bodyObserver;
            return;
        }

        if (headerPosition !== 'sticky') return;

        const sentinel = document.createElement('div');
        sentinel.style.position = 'absolute';
        sentinel.style.top = '50px';
        sentinel.style.height = '1px';
        sentinel.style.width = '1px';
        sentinel.style.pointerEvents = 'none';
        sentinel.style.visibility = 'hidden';
        document.body.insertBefore(sentinel, document.body.firstChild);

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (!entry.isIntersecting) {
                        header.classList.add('scrolled');
                    } else {
                        header.classList.remove('scrolled');
                    }
                });
            },
            {
                threshold: 0,
                rootMargin: '0px',
            },
        );

        observer.observe(sentinel);
        this.scrollObserver = observer;
        this.scrollSentinel = sentinel;
    }

    /**
     * Mobile: sticky top must match the visible bottom edge of <nova-header> (and optional
     * submenu section above mega). --nova-header-height only measures the inner .container
     * and undercounts when the host is fixed after scroll (component-header.js threshold 200px).
     */
    setupMobileStickyStack() {
        const wrapper = document.querySelector('.mm-wrapper');
        const novaHeader = document.querySelector('nova-header');
        if (!wrapper || !novaHeader) return;

        const MM_MOBILE_MAX = 1279;

        const applyStackTop = () => {
            if (this.mobileStackRaf) cancelAnimationFrame(this.mobileStackRaf);
            this.mobileStackRaf = requestAnimationFrame(() => {
                this.mobileStackRaf = 0;
                if (window.innerWidth > MM_MOBILE_MAX) {
                    wrapper.style.removeProperty('--mm-mobile-sticky-top');
                    return;
                }
                const nh = novaHeader.getBoundingClientRect();
                let topPx = Math.max(0, Math.ceil(nh.bottom));

                const megaSection =
                    wrapper.closest('.shopify-section.mm-section') ||
                    document.querySelector('.shopify-section.mm-section');
                const prev = megaSection?.previousElementSibling;
                if (prev?.classList?.contains('shopify-section')) {
                    const pb = prev.getBoundingClientRect().bottom;
                    topPx = Math.max(topPx, Math.ceil(pb));
                }

                wrapper.style.setProperty('--mm-mobile-sticky-top', `${topPx}px`);
            });
        };

        const applyAfterHeaderTransition = () => {
            applyStackTop();
            window.setTimeout(applyStackTop, 60);
            window.setTimeout(applyStackTop, 340);
        };

        this.mobileStackSync = applyStackTop;

        window.addEventListener('scroll', applyStackTop, { passive: true });
        window.addEventListener('resize', applyStackTop);

        if (typeof ResizeObserver !== 'undefined') {
            const ro = new ResizeObserver(applyStackTop);
            ro.observe(novaHeader);
            const headerSection = document.querySelector('#shopify-section-header');
            if (headerSection) ro.observe(headerSection);
            const megaSection =
                wrapper.closest('.shopify-section.mm-section') ||
                document.querySelector('.shopify-section.mm-section');
            const prev = megaSection?.previousElementSibling;
            if (prev?.classList?.contains('shopify-section')) ro.observe(prev);
            this.mobileStackResizeObserver = ro;
        }

        const mo = new MutationObserver(applyAfterHeaderTransition);
        mo.observe(document.body, {
            attributes: true,
            attributeFilter: ['is-scrolled'],
        });
        this.mobileStackBodyObserver = mo;

        applyStackTop();
        applyAfterHeaderTransition();
    }

    toggleMobileMenu() {
        const mobileToggle = document.getElementById('mm-mobile-toggle');
        const mobilePanel = document.getElementById('mm-mobile-panel');
        const mobileOverlay = document.getElementById('mm-mobile-overlay');

        if (!mobileToggle || !mobilePanel || !mobileOverlay) return;

        const isChecked = mobileToggle.checked;

        if (isChecked) {
            this.openMobileMenu();
        } else {
            this.closeMobileMenu();
        }
    }

    openMobileMenu() {
        const mobileToggleButton = document.getElementById('mm-toggle');
        const mobileToggle = document.getElementById('mm-mobile-toggle');
        const mobilePanel = document.getElementById('mm-mobile-panel');
        const mobileOverlay = document.getElementById('mm-mobile-overlay');

        if (!mobileToggle || !mobilePanel || !mobileOverlay) return;

        if (this.mobileCloseFadeTimer) {
            clearTimeout(this.mobileCloseFadeTimer);
            this.mobileCloseFadeTimer = null;
        }
        if (this._mobileCloseTransitionEnd) {
            mobilePanel.removeEventListener('transitionend', this._mobileCloseTransitionEnd);
            this._mobileCloseTransitionEnd = null;
        }
        mobilePanel.classList.remove('mm-mobile-panel--leaving');
        mobileOverlay.classList.remove('mm-mobile-overlay--leaving');
        mobilePanel.style.transition = '';
        mobileOverlay.style.transition = '';

        if (typeof this.mobileStackSync === 'function') {
            this.mobileStackSync();
        }

        // Set checkbox as checked and add active classes
        mobileToggle.checked = true;
        mobilePanel.classList.add('active');
        mobileOverlay.classList.add('active');

        // Prevent body scroll
        document.body.classList.add('mm-mobile-menu-open');

        // Update ARIA attributes
        if (mobileToggleButton) {
            mobileToggleButton.setAttribute('aria-expanded', 'true');
        }
        mobilePanel.setAttribute('aria-hidden', 'false');

        const closeBtn = document.getElementById('mm-mobile-close');
        const firstMenuItem = mobilePanel.querySelector('.mm-mobile-submenu-toggle, .mm-mobile-link');
        const focusTarget = closeBtn || firstMenuItem;
        if (focusTarget) {
            setTimeout(() => focusTarget.focus(), 100);
        }
    }

    closeMobileMenu(options = {}) {
        const immediate = options.immediate === true;
        const mobileToggleButton = document.getElementById('mm-toggle');
        const mobileToggle = document.getElementById('mm-mobile-toggle');
        const mobilePanel = document.getElementById('mm-mobile-panel');
        const mobileOverlay = document.getElementById('mm-mobile-overlay');

        if (!mobileToggle || !mobilePanel || !mobileOverlay) return;

        const finalize = () => {
            if (this.mobileCloseFadeTimer) {
                clearTimeout(this.mobileCloseFadeTimer);
                this.mobileCloseFadeTimer = null;
            }
            if (this._mobileCloseTransitionEnd) {
                mobilePanel.removeEventListener('transitionend', this._mobileCloseTransitionEnd);
                this._mobileCloseTransitionEnd = null;
            }
            if (
                !mobilePanel.classList.contains('active') &&
                !mobilePanel.classList.contains('mm-mobile-panel--leaving')
            ) {
                return;
            }

            mobileToggle.checked = false;
            mobilePanel.classList.remove('active', 'mm-mobile-panel--leaving');
            mobileOverlay.classList.remove('active', 'mm-mobile-overlay--leaving');
            mobilePanel.style.transition = 'none';
            mobileOverlay.style.transition = 'none';
            void mobilePanel.offsetHeight;
            mobilePanel.style.transition = '';
            mobileOverlay.style.transition = '';

            document.body.classList.remove('mm-mobile-menu-open');

            if (mobileToggleButton) {
                mobileToggleButton.setAttribute('aria-expanded', 'false');
            }
            mobilePanel.setAttribute('aria-hidden', 'true');

            this.closeAllMobileDropdowns();
        };

        if (!mobilePanel.classList.contains('active')) {
            mobilePanel.classList.remove('mm-mobile-panel--leaving');
            mobileOverlay.classList.remove('mm-mobile-overlay--leaving');
            return;
        }

        if (!immediate && mobilePanel.classList.contains('mm-mobile-panel--leaving')) return;

        if (immediate) {
            finalize();
            return;
        }

        mobileToggle.checked = false;
        if (mobileToggleButton) {
            mobileToggleButton.setAttribute('aria-expanded', 'false');
        }

        mobilePanel.classList.add('mm-mobile-panel--leaving');
        mobileOverlay.classList.add('mm-mobile-overlay--leaving');

        this._mobileCloseTransitionEnd = e => {
            if (e.target !== mobilePanel || e.propertyName !== 'opacity') return;
            finalize();
        };
        mobilePanel.addEventListener('transitionend', this._mobileCloseTransitionEnd);

        this.mobileCloseFadeTimer = setTimeout(() => {
            if (mobilePanel.classList.contains('mm-mobile-panel--leaving')) finalize();
        }, 400);
    }

    isMobileMenuOpen() {
        const mobilePanel = document.getElementById('mm-mobile-panel');
        return mobilePanel && mobilePanel.classList.contains('active');
    }

    toggleMobileDropdown(toggle) {
        const targetId = toggle.dataset.target;
        const dropdown = document.getElementById(targetId);

        if (!dropdown) return;

        const isActive = dropdown.classList.contains('active');

        // Close all other dropdowns first
        this.closeAllMobileDropdowns();

        if (!isActive) {
            dropdown.classList.add('active');
            toggle.classList.add('active');
            toggle.setAttribute('aria-expanded', 'true');
        } else {
            toggle.setAttribute('aria-expanded', 'false');
        }
    }

    closeAllMobileDropdowns() {
        const mobileDropdowns = document.querySelectorAll('.mm-mobile-dropdown');
        const mobileToggles = document.querySelectorAll('.mm-mobile-submenu-toggle');

        mobileDropdowns.forEach(dropdown => {
            dropdown.classList.remove('active');
        });

        mobileToggles.forEach(toggle => {
            toggle.classList.remove('active');
            toggle.setAttribute('aria-expanded', 'false');
        });
    }

    closeAllDropdowns() {
        const dropdowns = document.querySelectorAll('.mm-dropdown');
        const menuItems = document.querySelectorAll('.mm-item');

        dropdowns.forEach(dropdown => {
            dropdown.classList.remove('active');
        });

        menuItems.forEach(item => {
            item.classList.remove('active');
            // Reset click state for all items
            item.isClicked = false;
        });
    }

    handleKeyboardNavigation(e) {
        const mobilePanel = document.getElementById('mm-mobile-panel');
        if (!mobilePanel) return;

        const focusableElements = mobilePanel.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
            // Shift + Tab on first element - close menu and focus toggle
            e.preventDefault();
            this.closeMobileMenu();
            document.getElementById('mm-toggle')?.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
            // Tab on last element - close menu
            e.preventDefault();
            this.closeMobileMenu();
        }
    }

    // Public method to close menu programmatically
    closeMenu() {
        if (this.isMobileMenuOpen()) {
            this.closeMobileMenu();
        }
        this.closeAllDropdowns();
    }

    // Handle window resize
    handleResize() {
        // Close mobile menu if window becomes desktop size
        if (window.innerWidth >= 1280 && this.isMobileMenuOpen()) {
            this.closeMobileMenu({ immediate: true });
        }

        if (typeof this.mobileStackSync === 'function') {
            this.mobileStackSync();
        }
    }

    // Cleanup method for proper resource management
    destroy() {
        this.closeMobileMenu({ immediate: true });
        document.body.classList.remove('mm-mobile-menu-open');

        if (this._onDesktopOutsideClick) {
            document.removeEventListener('click', this._onDesktopOutsideClick);
            this._onDesktopOutsideClick = null;
        }
        if (this._onNovaMobileMenuClose) {
            document.removeEventListener('nova:mobile-menu:close', this._onNovaMobileMenuClose);
            this._onNovaMobileMenuClose = null;
        }
        if (this._onMobileOutsideClick) {
            document.removeEventListener('click', this._onMobileOutsideClick);
            this._onMobileOutsideClick = null;
        }
        if (this._onMobileEscapeKey) {
            document.removeEventListener('keydown', this._onMobileEscapeKey);
            this._onMobileEscapeKey = null;
        }
        if (this._onMobileTabKey) {
            document.removeEventListener('keydown', this._onMobileTabKey);
            this._onMobileTabKey = null;
        }

        if (this.mobileStackRaf) {
            cancelAnimationFrame(this.mobileStackRaf);
            this.mobileStackRaf = 0;
        }
        if (this.mobileStackResizeObserver) {
            this.mobileStackResizeObserver.disconnect();
            this.mobileStackResizeObserver = null;
        }
        if (this.mobileStackBodyObserver) {
            this.mobileStackBodyObserver.disconnect();
            this.mobileStackBodyObserver = null;
        }
        if (typeof this.mobileStackSync === 'function') {
            window.removeEventListener('scroll', this.mobileStackSync);
            window.removeEventListener('resize', this.mobileStackSync);
            this.mobileStackSync = null;
        }
        if (this.bodyScrollObserver) {
            this.bodyScrollObserver.disconnect();
            this.bodyScrollObserver = null;
        }
        if (this.scrollObserver) {
            this.scrollObserver.disconnect();
            this.scrollObserver = null;
        }
        if (this.scrollSentinel && this.scrollSentinel.parentNode) {
            this.scrollSentinel.parentNode.removeChild(this.scrollSentinel);
            this.scrollSentinel = null;
        }
        if (this.mobileCloseFadeTimer) {
            clearTimeout(this.mobileCloseFadeTimer);
            this.mobileCloseFadeTimer = null;
        }
        const closingPanel = document.getElementById('mm-mobile-panel');
        if (this._mobileCloseTransitionEnd && closingPanel) {
            closingPanel.removeEventListener('transitionend', this._mobileCloseTransitionEnd);
            this._mobileCloseTransitionEnd = null;
        }
    }
}

function isMegaMenuSectionElement(sectionEl) {
    return sectionEl?.classList?.contains('mm-section') && sectionEl.querySelector('.mm-wrapper');
}

function initMegaMenu() {
    if (!document.querySelector('.mm-wrapper')) return;
    if (window.megaMenu) {
        window.megaMenu.destroy();
    }
    window.megaMenu = new MegaMenu();
}

function teardownMegaMenuSection(event) {
    if (!isMegaMenuSectionElement(event.target)) return;
    if (window.megaMenu) {
        window.megaMenu.destroy();
        window.megaMenu = null;
    }
}

function bootMegaMenu() {
    initMegaMenu();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootMegaMenu);
} else {
    bootMegaMenu();
}

document.addEventListener('shopify:section:load', (event) => {
    if (!isMegaMenuSectionElement(event.target)) return;
    initMegaMenu();
});

document.addEventListener('shopify:section:select', (event) => {
    if (!isMegaMenuSectionElement(event.target)) return;
    initMegaMenu();
});

document.addEventListener('shopify:section:unload', teardownMegaMenuSection);

let megaMenuResizeTimeout;
window.addEventListener('resize', () => {
    if (!window.megaMenu) return;
    clearTimeout(megaMenuResizeTimeout);
    megaMenuResizeTimeout = setTimeout(() => {
        window.megaMenu.handleResize();
    }, 100);
});

// Handle page visibility change (mobile browsers)
document.addEventListener('visibilitychange', () => {
    if (document.hidden && window.megaMenu && window.megaMenu.isMobileMenuOpen()) {
        window.megaMenu.closeMobileMenu({ immediate: true });
    }
});

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
    module.exports = MegaMenu;
}
